import { type DocumentType } from './document-types.js'
import { type LegacyCredMap } from './internal.js'

export const defaultLTLServiceTypes = [
  'expedited',
  'guaranteed',
  'standard',
  'temp_control',
  'volume',
] as const
export type StandardLTLServiceTypes = (typeof defaultLTLServiceTypes)[number]

export const ltlServiceTypes = [
  ...defaultLTLServiceTypes,
  'accelerated',
  'accelerated_guaranteed',
  'deferred',
  'economy',
  'freight_economy',
  'freight_economy_am_delivery',
  'freight_economy_close_of_business',
  'freight_one_day',
  'freight_overnight',
  'freight_priority',
  'freight_priority_am_delivery',
  'freight_priority_close_of_business',
  'freight_three_day',
  'freight_two_day',
  'freight_two_day_am',
  'guaranteed_before_nine_am',
  'guaranteed_before_ten_am',
  'guaranteed_before_three_pm',
  'guaranteed_before_three_thirty_pm',
  'guaranteed_before_twelve_pm',
  'guaranteed_one_pm',
  'guaranteed_three_pm',
  'guaranteed_three_thirty_pm',
  'guaranteed_four_pm',
  'guaranteed_five_pm',
  'guaranteed_next_day',
  'guaranteed_eight_am',
  'guaranteed_nine_am',
  'guaranteed_ten_am',
  'guaranteed_ten_thirty_am',
  'guaranteed_eleven_am',
  'guaranteed_twelve_pm',
  'guaranteed_two_pm',
  'threshold',
  'time_critical',
  'time_critical_noon',
  'time_critical_twelve_pm',
  'time_critical_five_pm',
  'volume_truckload_basic',
  'volume_truckload_economy',
  'volume_truckload_exclusive_use',
  'volume_truckload_exempt',
  'volume_truckload_service',
  'volume_truckload_standard',
  'freight_direct_basic_economy',
  'freight_direct_basic_by_appointment_economy',
  'freight_direct_basic_priority',
  'freight_direct_basic_by_appointment_priority',
  'freight_direct_standard',
  'freight_direct_premium',
] as const
export type LTLServiceType = (typeof ltlServiceTypes)[number]

export const parcelServiceTypes = [
  'express_saver',
  'first_overnight',
  'ground',
  'home_delivery',
  'next_day_early',
  'next_day_saver',
  'next_day',
  'priority_overnight',
  'second_day_am',
  'second_day',
  'standard_overnight',
  'ground_saver',
  'three_day_select',
  'two_day_am',
  'two_day',
  'priority_overnight_early',
  'saturday_delivery',
  'saturday_delivery_early',
  'priority_twelve_pm',
] as const
export type ParcelServiceType = (typeof parcelServiceTypes)[number]
export type CarrierServiceType = LTLServiceType | ParcelServiceType

export interface CarrierService {
  id: CarrierServiceType
  name: string
  guaranteed?: boolean
  guaranteedByTime?: string
}

export const defaultCarrierServices: CarrierService[] = [
  {
    id: 'standard',
    name: 'Standard',
  },
  {
    id: 'guaranteed',
    name: 'Guaranteed',
  },
  {
    id: 'expedited',
    name: 'Expedited',
  },
  {
    id: 'temp_control',
    name: 'Temperature Controlled',
  },
  {
    id: 'volume',
    name: 'Volume',
  },
]

export interface CarrierDef<TCredType extends string = string> {
  scac: string
  name: string
  credentialTypes: readonly TCredType[]
  locationCredentialTypes?: readonly TCredType[]
  ltlServices?: CarrierService[]
  parcelServices?: CarrierService[]
  legacyCredMap?: LegacyCredMap<TCredType>
  engineLayers: EngineLayer[]
  documentTypes?: readonly DocumentType[]
  credentialConfig?: Partial<Record<TCredType, CredConfig>>
}

export interface CredConfig {
  hideFromFreightview?: boolean
  type?: 'text' | 'checkbox'
  nonsensitive?: boolean
  customerIdentifier?: boolean
}

export interface LegacyCredential {
  username?: string
  password?: string
  apiKey?: string
  apiKeys?: Array<{ keyType: string; value?: string | null }>
  apiPassword?: string
  accountNumber?: string
  spotVolumeAccountNumber?: string // engine only for odfl
  busId?: string // not used in this repo - but used elsewhere
  ref1?: string
  ref2?: string
  ref3?: string
  ref4?: string
  ref5?: string
  spotVolumeAPIKey?: string
  enableSpotVolumeRates?: boolean
  addressId?: string
  billTo?: {
    address?: string
    city?: string
    state?: string
    postalCode?: string
    country?: string
  }
  details?: CredentialDetail[]
  credentialId?: string
  customerCode?: string
  authenticationCode?: string
  token?: string
  email?: string
  companyName?: string
}

export interface CredentialDetail<T = string> {
  type: T
  value: string | boolean
  expires?: string
}

export type EngineLayer =
  | 'provisioning'
  | 'rating'
  | 'bol'
  | 'pickup'
  | 'cancel'
  | 'documents'
  | 'tracking'
  | 'profinder'
  | 'invoice'
  | 'pickup-status'
